import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdFileDownload, MdPrint } from 'react-icons/md';
import '../master/MasterData.css';

interface SalesByPeriod {
  period: string;
  invoiceCount: number;
  totalSales: number;
  totalCost: number;
  grossProfit: number;
  profitMargin: number;
}

interface SalesByProduct {
  itemCode: string;
  itemName: string;
  category: string;
  quantitySold: number;
  totalSales: number;
  totalCost: number;
  grossProfit: number;
}

interface SalesByCustomer {
  customerCode: string;
  customerName: string;
  invoiceCount: number;
  totalSales: number;
  totalCost: number;
  grossProfit: number;
  lastPurchaseDate: string;
}

const SalesReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'period' | 'product' | 'customer'>('period');
  const [dateFrom, setDateFrom] = useState('2025-01-01');
  const [dateTo, setDateTo] = useState('2025-03-31');
  const [periodType, setPeriodType] = useState<'daily' | 'weekly' | 'monthly'>('monthly');

  // Sample data - by period
  const salesByPeriod: SalesByPeriod[] = [
    {
      period: '2025-01',
      invoiceCount: 45,
      totalSales: 8750000,
      totalCost: 6125000,
      grossProfit: 2625000,
      profitMargin: 30.0
    },
    {
      period: '2025-02',
      invoiceCount: 52,
      totalSales: 9880000,
      totalCost: 6916000,
      grossProfit: 2964000,
      profitMargin: 30.0
    },
    {
      period: '2025-03',
      invoiceCount: 48,
      totalSales: 9240000,
      totalCost: 6468000,
      grossProfit: 2772000,
      profitMargin: 30.0
    }
  ];

  // Sample data - by product
  const salesByProduct: SalesByProduct[] = [
    {
      itemCode: 'FOAM-001',
      itemName: 'High Density Foam Mattress',
      category: 'Mattresses',
      quantitySold: 245,
      totalSales: 12250000,
      totalCost: 8575000,
      grossProfit: 3675000
    },
    {
      itemCode: 'FOAM-002',
      itemName: 'Memory Foam Pillow',
      category: 'Pillows',
      quantitySold: 580,
      totalSales: 5800000,
      totalCost: 4060000,
      grossProfit: 1740000
    },
    {
      itemCode: 'FOAM-003',
      itemName: 'Orthopedic Foam Cushion',
      category: 'Cushions',
      quantitySold: 420,
      totalSales: 4200000,
      totalCost: 2940000,
      grossProfit: 1260000
    },
    {
      itemCode: 'FOAM-004',
      itemName: 'Baby Foam Mattress',
      category: 'Mattresses',
      quantitySold: 180,
      totalSales: 2700000,
      totalCost: 1890000,
      grossProfit: 810000
    },
    {
      itemCode: 'FOAM-005',
      itemName: 'Seat Cushion Set',
      category: 'Cushions',
      quantitySold: 350,
      totalSales: 2920000,
      totalCost: 2044000,
      grossProfit: 876000
    }
  ];

  // Sample data - by customer
  const salesByCustomer: SalesByCustomer[] = [
    {
      customerCode: 'CUST001',
      customerName: 'ABC Furniture Corp.',
      invoiceCount: 24,
      totalSales: 9600000,
      totalCost: 6720000,
      grossProfit: 2880000,
      lastPurchaseDate: '2025-03-28'
    },
    {
      customerCode: 'CUST002',
      customerName: 'XYZ Home Depot',
      invoiceCount: 18,
      totalSales: 7200000,
      totalCost: 5040000,
      grossProfit: 2160000,
      lastPurchaseDate: '2025-03-25'
    },
    {
      customerCode: 'CUST003',
      customerName: 'Manila Bedding Supplies',
      invoiceCount: 15,
      totalSales: 5250000,
      totalCost: 3675000,
      grossProfit: 1575000,
      lastPurchaseDate: '2025-03-22'
    },
    {
      customerCode: 'CUST004',
      customerName: 'Cebu Furniture Outlet',
      invoiceCount: 12,
      totalSales: 3840000,
      totalCost: 2688000,
      grossProfit: 1152000,
      lastPurchaseDate: '2025-03-20'
    },
    {
      customerCode: 'CUST005',
      customerName: 'Davao Mattress Shop',
      invoiceCount: 10,
      totalSales: 2980000,
      totalCost: 2086000,
      grossProfit: 894000,
      lastPurchaseDate: '2025-03-18'
    }
  ];

  const totalPeriod = salesByPeriod.reduce((acc, item) => ({
    invoiceCount: acc.invoiceCount + item.invoiceCount,
    totalSales: acc.totalSales + item.totalSales,
    totalCost: acc.totalCost + item.totalCost,
    grossProfit: acc.grossProfit + item.grossProfit
  }), { invoiceCount: 0, totalSales: 0, totalCost: 0, grossProfit: 0 });

  const totalProduct = salesByProduct.reduce((acc, item) => ({
    quantitySold: acc.quantitySold + item.quantitySold,
    totalSales: acc.totalSales + item.totalSales,
    totalCost: acc.totalCost + item.totalCost,
    grossProfit: acc.grossProfit + item.grossProfit
  }), { quantitySold: 0, totalSales: 0, totalCost: 0, grossProfit: 0 });

  const totalCustomer = salesByCustomer.reduce((acc, item) => ({
    invoiceCount: acc.invoiceCount + item.invoiceCount,
    totalSales: acc.totalSales + item.totalSales,
    totalCost: acc.totalCost + item.totalCost,
    grossProfit: acc.grossProfit + item.grossProfit
  }), { invoiceCount: 0, totalSales: 0, totalCost: 0, grossProfit: 0 });

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Sales Reports</h2>
            <p className="subtitle">Analyze sales performance by period, product, and customer</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handlePrint}>
              <MdPrint /> Print
            </button>
            <button className="btn btn-primary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">📊</div>
            <div className="summary-content">
              <p className="summary-label">Total Sales</p>
              <p className="summary-value">₱{totalPeriod.totalSales.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <p className="summary-label">Gross Profit</p>
              <p className="summary-value" style={{ color: '#28a745' }}>
                ₱{totalPeriod.grossProfit.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📈</div>
            <div className="summary-content">
              <p className="summary-label">Profit Margin</p>
              <p className="summary-value" style={{ color: '#17a2b8' }}>
                {((totalPeriod.grossProfit / totalPeriod.totalSales) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📝</div>
            <div className="summary-content">
              <p className="summary-label">Total Invoices</p>
              <p className="summary-value">{totalPeriod.invoiceCount}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Date From</label>
              <input
                type="date"
                  lang="en-US"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Date To</label>
              <input
                type="date"
                  lang="en-US"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            {activeTab === 'period' && (
              <div className="form-group" style={{ minWidth: '150px' }}>
                <label>Period Type</label>
                <select
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value as 'daily' | 'weekly' | 'monthly')}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            )}
          </div>

          <div className="filter-tabs" style={{ marginBottom: '20px' }}>
            <button
              className={`filter-tab ${activeTab === 'period' ? 'active' : ''}`}
              onClick={() => setActiveTab('period')}
            >
              By Period
            </button>
            <button
              className={`filter-tab ${activeTab === 'product' ? 'active' : ''}`}
              onClick={() => setActiveTab('product')}
            >
              By Product
            </button>
            <button
              className={`filter-tab ${activeTab === 'customer' ? 'active' : ''}`}
              onClick={() => setActiveTab('customer')}
            >
              By Customer
            </button>
          </div>

          {activeTab === 'period' && (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Invoice Count</th>
                    <th>Total Sales</th>
                    <th>Total Cost</th>
                    <th>Gross Profit</th>
                    <th>Profit Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByPeriod.map((item, index) => (
                    <tr key={index}>
                      <td className="name">{item.period}</td>
                      <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.invoiceCount}</td>
                      <td className="amount">₱{item.totalSales.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#dc3545' }}>₱{item.totalCost.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#28a745', fontWeight: 'bold' }}>
                        ₱{item.grossProfit.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'center', color: '#17a2b8', fontWeight: 'bold' }}>
                        {item.profitMargin.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td>TOTAL:</td>
                    <td style={{ textAlign: 'center' }}>{totalPeriod.invoiceCount}</td>
                    <td className="amount">₱{totalPeriod.totalSales.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#dc3545' }}>₱{totalPeriod.totalCost.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#28a745' }}>₱{totalPeriod.grossProfit.toLocaleString()}</td>
                    <td style={{ textAlign: 'center', color: '#17a2b8' }}>
                      {((totalPeriod.grossProfit / totalPeriod.totalSales) * 100).toFixed(1)}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {activeTab === 'product' && (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Category</th>
                    <th>Qty Sold</th>
                    <th>Total Sales</th>
                    <th>Total Cost</th>
                    <th>Gross Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByProduct.map((item, index) => (
                    <tr key={index}>
                      <td className="code">{item.itemCode}</td>
                      <td className="name">{item.itemName}</td>
                      <td>{item.category}</td>
                      <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.quantitySold}</td>
                      <td className="amount">₱{item.totalSales.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#dc3545' }}>₱{item.totalCost.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#28a745', fontWeight: 'bold' }}>
                        ₱{item.grossProfit.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td colSpan={3}>TOTAL:</td>
                    <td style={{ textAlign: 'center' }}>{totalProduct.quantitySold}</td>
                    <td className="amount">₱{totalProduct.totalSales.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#dc3545' }}>₱{totalProduct.totalCost.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#28a745' }}>₱{totalProduct.grossProfit.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {activeTab === 'customer' && (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer Code</th>
                    <th>Customer Name</th>
                    <th>Invoice Count</th>
                    <th>Total Sales</th>
                    <th>Total Cost</th>
                    <th>Gross Profit</th>
                    <th>Last Purchase</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByCustomer.map((item, index) => (
                    <tr key={index}>
                      <td className="code">{item.customerCode}</td>
                      <td className="name">{item.customerName}</td>
                      <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.invoiceCount}</td>
                      <td className="amount">₱{item.totalSales.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#dc3545' }}>₱{item.totalCost.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#28a745', fontWeight: 'bold' }}>
                        ₱{item.grossProfit.toLocaleString()}
                      </td>
                      <td>{new Date(item.lastPurchaseDate).toLocaleDateString('en-US')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td colSpan={2}>TOTAL:</td>
                    <td style={{ textAlign: 'center' }}>{totalCustomer.invoiceCount}</td>
                    <td className="amount">₱{totalCustomer.totalSales.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#dc3545' }}>₱{totalCustomer.totalCost.toLocaleString()}</td>
                    <td className="amount" style={{ color: '#28a745' }}>₱{totalCustomer.grossProfit.toLocaleString()}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default SalesReports;
